import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  Building2,
  Globe,
  Clock,
  Eye,
  Menu,
  X,
  MessageCircle,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  Sparkles,
  Zap,
  Users,
  Shield,
  Facebook,
  Instagram,
  Home,
  FileText,
  BarChart3,
  Trophy,
  Smile,
  Coffee,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Code2,
  Bot,
  Database,
  Workflow,
  PieChart,
  UserCheck,
  RefreshCw,
  Plug,
  Calendar,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { InteractiveParticles } from "@/components/InteractiveParticles";
import { AnimatedFooter } from "@/components/ui/animated-footer";

// Testimonial Photos
import testimonialMaria from "@/assets/testimonials/maria.jpg";
import testimonialCarlos from "@/assets/testimonials/carlos.jpg";
import testimonialAna from "@/assets/testimonials/ana.jpg";
import logoSfundo from "@/assets/logo_sfundo.png";
import itacondoImg from "@/assets/products/itacondo.png";
import person1 from "@/assets/testimonials/person1.jpg";
import person2 from "@/assets/testimonials/person2.jpg";
import person3 from "@/assets/testimonials/person3.jpg";
import person4 from "@/assets/testimonials/person4.jpg";

const testimonialsList = [
  { dl: 0, photo: testimonialMaria, q: "O ItaCondo revolucionou a administração do nosso condomínio. A portaria virou tranquilidade e os relatórios financeiros são impecáveis.", n: "Roberto Morales", r: "Síndico — Residencial Parque Verde" },
  { dl: 120, photo: testimonialCarlos, q: "Reduzimos em 70% o tempo gasto com a gestão financeira. O sistema é intuitivo e a equipe resolve qualquer coisa na hora, com proximidade de verdade.", n: "Carlos Eduardo", r: "Administrador — Condomínio Solar" },
  { dl: 240, photo: testimonialAna, q: "A comunicação com os moradores melhorou drasticamente. As notificações por app chegam na hora e o suporte parece um amigo. Tecnologia com gente de verdade.", n: "Ana Beatriz", r: "Síndica — Edifício Montezuma" },
  { dl: 360, photo: null, q: "A unificação do nosso sistema de cobrança do sindicato foi um divisor de águas. O trabalho foi realizado de forma extremamente rápida, simplificando processos que antes eram manuais e demorados. Recomendo fortemente!", n: "Jovino SEEN", r: "Diretor — Sindicato SEEN" },
  { dl: 480, photo: null, q: "Antes de usar a plataforma, sofríamos diariamente com controle de ponto manual e extravio de encomendas na portaria. O sistema unificou tudo e veio para simplificar de verdade o nosso dia a dia.", n: "Rafaela Oliveira", r: "Síndica — Condomínio Veneza 2" }
];

const Landing = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visibleSlides, setVisibleSlides] = useState(3);
  const [isAutoplayPaused, setIsAutoplayPaused] = useState(false);
  
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    condo: "",
  });

  // Redirecionar usuários logados para o dashboard
  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  // Intersection Observer para animações de revelação e contadores
  useEffect(() => {
    const run = () => {
      const els = document.querySelectorAll("[data-reveal]");
      els.forEach((el: any) => {
        el.style.opacity = "0";
        el.style.transform = "translateY(30px)";
        el.style.transition = "opacity .75s cubic-bezier(.2,.7,.2,1), transform .75s cubic-bezier(.2,.7,.2,1)";
        const dl = el.getAttribute("data-reveal-delay");
        if (dl) el.style.transitionDelay = dl + "ms";
        el.style.willChange = "opacity, transform";
      });

      const io = new IntersectionObserver(
        (ents) => {
          ents.forEach((e) => {
            if (e.isIntersecting) {
              (e.target as any).style.opacity = "1";
              (e.target as any).style.transform = "none";
              io.unobserve(e.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -7% 0px" }
      );

      els.forEach((el) => io.observe(el));

      const countElements = document.querySelectorAll("[data-count]");
      const countObserver = new IntersectionObserver(
        (ents, obs) => {
          ents.forEach((e) => {
            if (e.isIntersecting) {
              animateCount(e.target as HTMLElement);
              obs.unobserve(e.target);
            }
          });
        },
        { threshold: 0.6 }
      );

      countElements.forEach((el) => countObserver.observe(el));
    };

    const animateCount = (el: HTMLElement) => {
      const target = parseFloat(el.getAttribute("data-count") || "0");
      const pre = el.getAttribute("data-pre") || "";
      const suf = el.getAttribute("data-suf") || "";
      const sep = el.getAttribute("data-sep") === "true";
      const dur = 1500;
      const start = performance.now();

      const fmt = (n: number) => (sep ? Math.round(n).toLocaleString("pt-BR") : Math.round(n).toString());

      const tick = (t: number) => {
        const p = Math.min(1, (t - start) / dur);
        const e = 1 - Math.pow(1 - p, 3); // easeOutCubic
        el.textContent = pre + fmt(target * e) + suf;
        if (p < 1) requestAnimationFrame(tick);
      };

      requestAnimationFrame(tick);
    };

    setTimeout(run, 80);
  }, []);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.phone || !contactForm.condo) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    setIsSubmittingContact(true);
    
    const messageContent = `Solicitação de demonstração.\nCondomínio: ${contactForm.condo}\nWhatsApp: ${contactForm.phone}`;

    try {
      const response = await fetch("https://umnoiivuntnvsxkysvrr.supabase.co/functions/v1/send-contact-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: contactForm.name,
          email: contactForm.email,
          phone: contactForm.phone,
          message: messageContent,
        }),
      });
      const responseData = await response.json();
      if (response.ok) {
        toast.success("Demonstração agendada com sucesso! Entraremos em contato em breve.");
        setContactForm({
          name: "",
          email: "",
          phone: "",
          condo: "",
        });
      } else {
        if (responseData.error === "Domain verification required") {
          toast.error("Configuração de email pendente. Entre em contato via WhatsApp.");
        } else {
          toast.error(responseData.message || "Erro ao enviar mensagem. Tente novamente.");
        }
      }
    } catch (error) {
      console.error("Error sending contact email:", error);
      toast.error("Erro ao enviar mensagem. Tente novamente.");
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleWhatsAppContact = () => {
    window.open(
      "https://web.whatsapp.com/send?phone=5521998231720&text=Ol%C3%A1%20Gostaria%20de%20conhecer%20mais%20sobre%20o%20sistema%20itaCondo.",
      "_blank"
    );
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
      });
    }
    setIsMenuOpen(false);
  };

  // Carousel resize listener
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleSlides(1);
      } else if (window.innerWidth < 1024) {
        setVisibleSlides(2);
      } else {
        setVisibleSlides(3);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Autoplay effect
  useEffect(() => {
    if (isAutoplayPaused) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const maxIndex = testimonialsList.length - visibleSlides;
        if (maxIndex <= 0) return 0;
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 4500);

    return () => clearInterval(interval);
  }, [visibleSlides, isAutoplayPaused]);

  // Adjust slide position on window resizing
  useEffect(() => {
    const maxIndex = testimonialsList.length - visibleSlides;
    if (currentSlide > maxIndex) {
      setCurrentSlide(Math.max(0, maxIndex));
    }
  }, [visibleSlides, currentSlide]);

  const nextSlide = () => {
    const maxIndex = testimonialsList.length - visibleSlides;
    setCurrentSlide((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    const maxIndex = testimonialsList.length - visibleSlides;
    setCurrentSlide((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const renderIcon = (name: string, size = 22, className = "") => {
    switch (name) {
      case "shield":
        return <Shield className={className} size={size} />;
      case "userCheck":
        return <UserCheck className={className} size={size} />;
      case "calendar":
        return <Calendar className={className} size={size} />;
      case "scanFace":
        return <Eye className={className} size={size} />;
      case "wallet":
        return <BarChart3 className={className} size={size} />;
      case "fileText":
        return <FileText className={className} size={size} />;
      case "mapPin":
        return <MapPin className={className} size={size} />;
      case "refresh":
        return <RefreshCw className={className} size={size} />;
      case "globe":
        return <Globe className={className} size={size} />;
      case "code":
        return <Code2 className={className} size={size} />;
      case "bot":
        return <Bot className={className} size={size} />;
      case "bar":
        return <BarChart3 className={className} size={size} />;
      case "git":
        return <Workflow className={className} size={size} />;
      case "db":
        return <Database className={className} size={size} />;
      case "zap":
        return <Zap className={className} size={size} />;
      case "plug":
        return <Plug className={className} size={size} />;
      case "check":
        return <CheckCircle className={className} size={size} />;
      case "phone":
        return <Phone className={className} size={size} />;
      case "mail":
        return <Mail className={className} size={size} />;
      case "arrow":
        return <ArrowRight className={className} size={size} />;
      case "wa":
        return <MessageCircle className={className} size={size} />;
      case "logo":
        return <Building2 className={className} size={size} />;
      default:
        return null;
    }
  };

  const serviceMenuItems = [
    { label: "Gestão de Condomínios", href: "/servicos/gestao-condominios", icon: Building2, tone: "from-primary to-blue-500" },
    { label: "Webdesign & Landing Pages", href: "/servicos/webdesign", icon: Globe, tone: "from-cyan-400 to-blue-500" },
    { label: "Sistemas Web (SaaS/ERP/CRM)", href: "/servicos/sistemas-web", icon: Code2, tone: "from-violet-500 to-fuchsia-500" },
    { label: "Chatbots com IA", href: "/servicos/chatbots", icon: Bot, tone: "from-emerald-400 to-teal-500" },
    { label: "Power BI & Dashboards", href: "/servicos/power-bi", icon: PieChart, tone: "from-amber-400 to-orange-500" },
    { label: "ETL Java + Python", href: "/servicos/etl", icon: Database, tone: "from-sky-400 to-indigo-500" },
    { label: "Automação de Processos", href: "/servicos/automacao", icon: Workflow, tone: "from-pink-500 to-rose-500" },
    { label: "Integrações por API", href: "/servicos/integracoes", icon: Plug, tone: "from-blue-600 to-indigo-600" },
  ];

  const navItems: Array<{ label: string; id: string; hasSubmenu?: boolean }> = [
    { label: "Início", id: "hero" },
    { label: "Recursos", id: "recursos" },
    { label: "Ponto Digital", id: "ponto-digital" },
    { label: "Serviços", id: "servicos", hasSubmenu: true },
    { label: "Diferenciais", id: "diferenciais" },
    { label: "Clientes", id: "depoimentos" },
    { label: "Contato", id: "contato" },
  ];

  const marqueeTexts = [
    "Portaria 24/7",
    "Controle financeiro",
    "Ponto facial",
    "Reserva de áreas",
    "App do morador",
    "Encomendas",
    "Assembleias online",
    "Relatórios em PDF",
    "Comunicados",
    "Controle de acesso",
  ];

  const statsList = [
    { pre: "+", n: 10, suf: "", sep: false, display: "+10", l: "Anos de mercado" },
    { pre: "", n: 95, suf: "%", sep: false, display: "95%", l: "De satisfação" },
    { pre: "+", n: 1780, suf: "", sep: true, display: "+1.780", l: "Clientes ativos" },
    { pre: "+", n: 2000, suf: "", sep: true, display: "+2.000", l: "Projetos entregues" },
  ];

  const featuresList = [
    { dl: 0, icon: "shield", t: "Gerenciamento de Portaria", d: "Controle completo de entradas, encomendas e correspondências em tempo real." },
    { dl: 70, icon: "userCheck", t: "Controle de Acesso de Visitantes", d: "Cadastro, autorização e histórico de visitantes — liberação em segundos." },
    { dl: 140, icon: "calendar", t: "Reserva de Áreas Comuns", d: "Agendamento de áreas com regras, taxas e aprovação automática, sem conflitos." },
    { dl: 0, icon: "scanFace", t: "Ponto Eletrônico Facial", d: "Registro de ponto dos colaboradores com reconhecimento facial e geolocalização." },
    { dl: 70, icon: "wallet", t: "Controle Financeiro Completo", d: "Receitas, despesas, cobranças e inadimplência num painel claro, por unidade." },
    { dl: 140, icon: "fileText", t: "Relatórios Gerenciais", d: "Relatórios detalhados em PDF com indicadores de desempenho do condomínio." },
  ];

  const pontoFeaturesList = [
    { icon: "shield", t: "Prevenção antifraude ativa", d: "Reconhece fotos, vídeos ou telas digitais com prova de vida e bloqueia tentativas de fraude." },
    { icon: "mapPin", t: "Cerca geográfica integrada", d: "Garante que o colaborador está realmente no local de trabalho ao registrar o ponto." },
    { icon: "refresh", t: "Sincronização offline automática", d: "Registra mesmo sem internet e sincroniza com a nuvem assim que a conexão volta." },
  ];

  const servicesList = [
    { dl: 0, num: "01", icon: "globe", t: "Webdesign e Criação de Sites", d: "Sites institucionais e landing pages que convertem, com performance e SEO.", slug: "webdesign" },
    { dl: 60, num: "02", icon: "code", t: "Sistemas Web sob medida", d: "Plataformas multi-tenant com RLS, painel admin e APIs. Arquitetura escalável.", slug: "sistemas-web" },
    { dl: 120, num: "03", icon: "bot", t: "Chatbots Inteligentes", d: "Atendimento automatizado integrado ao WhatsApp e ao seu site, 24/7.", slug: "chatbots" },
    { dl: 180, num: "04", icon: "bar", t: "Dashboards em Power BI", d: "Visão completa do negócio em tempo real, com dados que viram decisão.", slug: "power-bi" },
    { dl: 0, num: "05", icon: "git", t: "Workflow", d: "Integração e tratamento de dados entre sistemas, sem trabalho manual.", slug: "etl" },
    { dl: 60, num: "06", icon: "db", t: "Banco de Dados (DBA)", d: "Performance, segurança e backup em PostgreSQL, SQL Server e Oracle.", slug: "dba" },
    { dl: 120, num: "07", icon: "zap", t: "Automação de Processos", d: "Fluxos integrados via CRM, ERP e WhatsApp, eliminando tarefas repetitivas.", slug: "automacao" },
    { dl: 180, num: "08", icon: "plug", t: "Integrações por API", d: "Conecte a ItaCondo a qualquer ferramenta que sua operação já usa.", slug: "integracoes" },
  ];

  return (
    <div 
      className="min-h-screen text-white overflow-x-hidden relative"
      style={{
        "--bg": "#06090F",
        "--panel": "#0E1626",
        "--panel2": "#131F35",
        "--line": "rgba(255,255,255,.09)",
        "--line2": "rgba(255,255,255,.14)",
        "--ink": "#EAF0FB",
        "--muted": "#90A0BA",
        "--blue": "#3B7BFF",
        "--blueSoft": "rgba(59,123,255,.13)",
        "--green": "#2BD27A",
        background: "var(--bg)",
        fontFamily: "'Manrope', sans-serif"
      } as React.CSSProperties}
    >
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(26px); }
          to { opacity: 1; transform: none; }
        }
        @keyframes floaty {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }
        @keyframes pulseRing {
          0% { box-shadow: 0 0 0 0 rgba(43, 210, 122, 0.5); }
          70% { box-shadow: 0 0 0 16px rgba(43, 210, 122, 0); }
          100% { box-shadow: 0 0 0 0 rgba(43, 210, 122, 0); }
        }
        @keyframes scanline {
          0% { top: 8%; }
          100% { top: 88%; }
        }
        @keyframes gradText {
          to { background-position: 200% center; }
        }
        @keyframes aurora1 {
          0% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(60px, -40px) scale(1.18); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes aurora2 {
          0% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-70px, 40px) scale(1.22); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes marquee {
          to { transform: translateX(-50%); }
        }
        @keyframes marqueeReverse {
          from { transform: translateX(-50%); }
          to { transform: translateX(0); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-160%) skewX(-18deg); }
          60%, 100% { transform: translateX(360%) skewX(-18deg); }
        }
        @keyframes spinSlow {
          to { transform: rotate(360deg); }
        }

        .feature-card:hover {
          transform: translateY(-6px) !important;
          border-color: rgba(59, 123, 255, 0.45) !important;
          box-shadow: 0 20px 44px rgba(0, 0, 0, 0.4) !important;
        }
        .service-card:hover {
          transform: translateY(-6px) !important;
          border-color: rgba(59, 123, 255, 0.45) !important;
          box-shadow: 0 20px 44px rgba(0, 0, 0, 0.4) !important;
        }
        .testimonial-card:hover {
          transform: translateY(-4px) !important;
          border-color: var(--line2) !important;
        }
        
        @media (max-width: 991px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
            gap: 50px !important;
          }
          .hero-container {
            padding: 80px 24px 50px !important;
          }
          .ponto-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          .cta-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
        }

        @media (max-width: 1024px) and (min-width: 769px) {
          .testimonials-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .stats-grid > div {
            border-right: none !important;
            border-bottom: 1px solid var(--line) !important;
          }
          .stats-grid > div:nth-child(2n) {
            border-right: none !important;
          }
          .stats-grid > div:nth-last-child(-n+2) {
            border-bottom: none !important;
          }
          .features-grid {
            grid-template-columns: 1fr !important;
          }
          .services-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .testimonials-grid {
            grid-template-columns: 1fr !important;
          }
        }
        
        @media (max-width: 520px) {
          .services-grid {
            grid-template-columns: 1fr !important;
          }
          .hero-title {
            font-size: 38px !important;
          }
        }
      `}} />

      {/* Floating WhatsApp Button */}
      <button
        onClick={handleWhatsAppContact}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30 transition-all duration-300 hover:scale-110"
        aria-label="Contato WhatsApp"
      >
        <MessageCircle className="h-7 w-7 text-white" />
      </button>

      {/* Header */}
      <header className="fixed top-4 left-4 right-4 z-50 bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Link to="/" className="flex items-center">
              <img 
                src={logoSfundo} 
                alt="itaCondo" 
                style={{ height: "45px", width: "auto", display: "block" }} 
              />
            </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) =>
              item.hasSubmenu ? (
                <div key={item.id} className="group relative">
                  <button
                    onClick={() => scrollToSection(item.id)}
                    className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors text-sm font-medium"
                  >
                    {item.label}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                  </button>
                  {/* Dropdown */}
                  <div className="invisible absolute left-1/2 top-full z-50 mt-3 w-[340px] -translate-x-1/2 translate-y-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    <div className="rounded-2xl border border-white/10 bg-slate-900/95 p-2 shadow-2xl backdrop-blur-xl">
                      {serviceMenuItems.map((s) => {
                        const Icon = s.icon;
                        return (
                          <Link
                            key={s.href}
                            to={s.href}
                            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-200 transition hover:bg-white/5 hover:text-white"
                          >
                            <span className={`grid size-9 shrink-0 place-items-center rounded-lg bg-gradient-to-br ${s.tone} text-white shadow-md`}>
                              <Icon className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 flex-1 truncate font-medium">{s.label}</span>
                            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
                >
                  {item.label}
                </button>
              )
            )}
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <Link 
                to="/login" 
                style={{ fontSize: "14.5px", color: "var(--muted)", fontWeight: 600, cursor: "pointer", transition: "color .2s" }}
                className="hover:text-white"
              >
                Entrar
              </Link>
              <div 
                onClick={() => scrollToSection("contato")}
                className="btn-hover"
                style={{ 
                  position: "relative", 
                  overflow: "hidden", 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "8px", 
                  padding: "11px 20px", 
                  borderRadius: "11px", 
                  background: "var(--blue)", 
                  color: "#fff", 
                  fontWeight: 700, 
                  fontSize: "14px", 
                  cursor: "pointer", 
                  boxShadow: "0 8px 22px rgba(59,123,255,.38)", 
                  transition: "transform .2s,box-shadow .2s" 
                }}
              >
                <span style={{ position: "absolute", top: 0, left: 0, width: "40%", height: "100%", background: "linear-gradient(105deg,transparent,rgba(255,255,255,.45),transparent)", animation: "shimmer 4s infinite" }}></span>
                Agendar demonstração
              </div>
            </div>
          </nav>

            {/* Mobile menu button */}
            <button className="lg:hidden p-2 text-white" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="lg:hidden py-4 border-t border-white/10 animate-fade-in">
              <div className="flex flex-col space-y-3">
                {navItems.map((item) =>
                  item.hasSubmenu ? (
                    <div key={item.id} className="flex flex-col">
                      <button
                        onClick={() => setIsMobileServicesOpen((v) => !v)}
                        className="flex items-center justify-between text-slate-300 hover:text-white transition-colors text-left py-2"
                      >
                        <span>{item.label}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${isMobileServicesOpen ? "rotate-180" : ""}`} />
                      </button>
                      {isMobileServicesOpen && (
                        <div className="mt-1 ml-2 flex flex-col gap-1 border-l border-white/10 pl-3 animate-fade-in">
                          {serviceMenuItems.map((s) => {
                            const Icon = s.icon;
                            return (
                              <Link
                                key={s.href}
                                to={s.href}
                                onClick={() => setIsMenuOpen(false)}
                                className="flex items-center gap-2.5 rounded-lg px-2 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-white"
                              >
                                <span className={`grid size-7 shrink-0 place-items-center rounded-md bg-gradient-to-br ${s.tone} text-white`}>
                                  <Icon className="h-3.5 w-3.5" />
                                </span>
                                <span className="truncate">{s.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="text-slate-300 hover:text-white transition-colors text-left py-2"
                    >
                      {item.label}
                    </button>
                  )
                )}
                <div className="flex flex-col gap-3 pt-2">
                  <Link 
                    to="/login" 
                    onClick={() => setIsMenuOpen(false)}
                    style={{ fontSize: "14.5px", color: "var(--muted)", fontWeight: 600, cursor: "pointer", textAlign: "center", padding: "10px 0", transition: "color .2s" }}
                    className="hover:text-white"
                  >
                    Entrar
                  </Link>
                  <div 
                    onClick={() => { scrollToSection("contato"); setIsMenuOpen(false); }}
                    className="btn-hover"
                    style={{ 
                      position: "relative", 
                      overflow: "hidden", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center",
                      gap: "8px", 
                      padding: "12px 20px", 
                      borderRadius: "11px", 
                      background: "var(--blue)", 
                      color: "#fff", 
                      fontWeight: 700, 
                      fontSize: "14px", 
                      cursor: "pointer", 
                      boxShadow: "0 8px 22px rgba(59,123,255,.38)", 
                      transition: "transform .2s,box-shadow .2s" 
                    }}
                  >
                    <span style={{ position: "absolute", top: 0, left: 0, width: "40%", height: "100%", background: "linear-gradient(105deg,transparent,rgba(255,255,255,.45),transparent)", animation: "shimmer 4s infinite" }}></span>
                    Agendar demonstração
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* HERO SECTION */}
      <div id="hero" className="hero-container" style={{ position: "relative", padding: "140px 56px 70px", overflow: "hidden" }}>
        <InteractiveParticles />
        <div style={{ position: "absolute", width: "620px", height: "620px", left: "-160px", top: "-200px", borderRadius: "50%", background: "radial-gradient(closest-side,rgba(59,123,255,.30),transparent)", filter: "blur(20px)", animation: "aurora1 16s ease-in-out infinite", pointerEvents: "none" }}></div>
        <div style={{ position: "absolute", width: "520px", height: "520px", right: "-120px", top: "-80px", borderRadius: "50%", background: "radial-gradient(closest-side,rgba(43,210,122,.18),transparent)", filter: "blur(20px)", animation: "aurora2 18s ease-in-out infinite", pointerEvents: "none" }}></div>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,.05) 1px,transparent 1px)", backgroundSize: "30px 30px", opacity: .6, maskImage: "radial-gradient(900px 500px at 30% 10%,#000,transparent 80%)", pointerEvents: "none" }}></div>

        <div className="hero-grid" style={{ position: "relative", display: "grid", gridTemplateColumns: "1.05fr .95fr", gap: "50px", alignItems: "center" }}>
          <div>
            <div style={{ opacity: 0, animation: "fadeUp .7s .05s both", display: "inline-flex", alignItems: "center", gap: "9px", padding: "8px 15px", borderRadius: "100px", background: "var(--blueSoft)", border: "1px solid rgba(59,123,255,.32)", fontSize: "12.5px", fontWeight: 700, letterSpacing: ".04em", color: "#9DBEFF", textTransform: "uppercase" }}>
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "var(--green)", boxShadow: "0 0 12px var(--green)", animation: "pulseRing 2.6s infinite" }}></span>
              Gestão de condomínios + Ponto facial
            </div>
            <h1 className="hero-title" style={{ opacity: 0, animation: "fadeUp .7s .14s both", fontSize: "55px", fontWeight: 800, margin: "22px 0 0", lineHeight: 1.03, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              A gestão do seu<br />condomínio,<br />
              <span style={{ background: "linear-gradient(100deg,var(--blue),#9DC0FF,var(--blue))", backgroundSize: "200% auto", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", animation: "gradText 7s linear infinite" }}>
                sob controle total.
              </span>
            </h1>
            <p style={{ opacity: 0, animation: "fadeUp .7s .24s both", fontSize: "18px", lineHeight: 1.6, color: "var(--muted)", marginTop: "22px", maxWidth: "520px" }}>
              Portaria, financeiro, áreas comuns, comunicação com moradores e ponto eletrônico com reconhecimento facial — tudo em um só sistema, com o suporte de quem trata cada cliente como parceiro há mais de 10 anos.
            </p>
            <div style={{ opacity: 0, animation: "fadeUp .7s .34s both", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "14px", marginTop: "34px" }}>
              <div 
                onClick={() => scrollToSection("contato")}
                className="btn-hero-primary" 
                style={{ position: "relative", overflow: "hidden", display: "flex", alignItems: "center", gap: "9px", padding: "16px 28px", borderRadius: "13px", background: "var(--blue)", color: "#fff", fontWeight: 700, fontSize: "15.5px", cursor: "pointer", boxShadow: "0 14px 34px rgba(59,123,255,.42)", transition: "transform .2s,box-shadow .2s" }}
              >
                <span style={{ position: "absolute", top: 0, left: 0, width: "40%", height: "100%", background: "linear-gradient(105deg,transparent,rgba(255,255,255,.5),transparent)", animation: "shimmer 3.6s infinite" }}></span>
                Agendar demonstração gratuita {renderIcon("arrow", 18)}
              </div>
              <div 
                onClick={handleWhatsAppContact}
                className="btn-hero-secondary" 
                style={{ display: "flex", alignItems: "center", gap: "9px", padding: "16px 24px", borderRadius: "13px", background: "rgba(43,210,122,.12)", border: "1px solid rgba(43,210,122,.3)", color: "var(--green)", fontWeight: 700, fontSize: "15px", cursor: "pointer", transition: "background .2s" }}
              >
                {renderIcon("wa", 18)} Falar no WhatsApp
              </div>
            </div>
            <div style={{ opacity: 0, animation: "fadeUp .7s .44s both", display: "flex", alignItems: "center", gap: "18px", marginTop: "32px" }}>
              <div style={{ display: "flex" }}>
                <img src={person1} alt="Morador" style={{ width: "36px", height: "36px", borderRadius: "50%", border: "2px solid var(--bg)", objectFit: "cover" }} />
                <img src={person2} alt="Síndico" style={{ width: "36px", height: "36px", borderRadius: "50%", border: "2px solid var(--bg)", marginLeft: "-12px", objectFit: "cover" }} />
                <img src={person3} alt="Administrador" style={{ width: "36px", height: "36px", borderRadius: "50%", border: "2px solid var(--bg)", marginLeft: "-12px", objectFit: "cover" }} />
                <img src={person4} alt="Colaborador" style={{ width: "36px", height: "36px", borderRadius: "50%", border: "2px solid var(--bg)", marginLeft: "-12px", objectFit: "cover" }} />
              </div>
              <div style={{ fontSize: "13.5px", color: "var(--muted)", lineHeight: 1.4 }}>
                <span style={{ color: "#FFC857" }}>★★★★★</span> 95% de satisfação<br />
                <b style={{ color: "var(--ink)" }}>+1.780 condomínios e empresas</b> confiam na ItaCondo
              </div>
            </div>
          </div>

          {/* Product Mockup */}
          <div style={{ position: "relative", opacity: 0, animation: "fadeUp .8s .3s both" }}>
            <div style={{ position: "absolute", inset: "-30px", background: "radial-gradient(closest-side,rgba(59,123,255,.3),transparent)", filter: "blur(10px)", pointerEvents: "none" }}></div>
            <div style={{ position: "relative", borderRadius: "18px", background: "linear-gradient(180deg,var(--panel2),var(--panel))", border: "1px solid var(--line2)", boxShadow: "0 36px 80px rgba(0,0,0,.55)", overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "7px", padding: "13px 16px", borderBottom: "1px solid var(--line)" }}>
                <span style={{ width: "11px", height: "11px", borderRadius: "50%", background: "#ff5f57" }}></span>
                <span style={{ width: "11px", height: "11px", borderRadius: "50%", background: "#febc2e" }}></span>
                <span style={{ width: "11px", height: "11px", borderRadius: "50%", background: "#28c840" }}></span>
                <span style={{ marginLeft: "10px", fontSize: "12px", color: "var(--muted)" }}>painel.itacondo.com.br</span>
              </div>
              <img 
                src={itacondoImg} 
                alt="Painel itaCondo" 
                style={{ width: "100%", height: "auto", display: "block", borderBottomLeftRadius: "18px", borderBottomRightRadius: "18px" }}
              />
            </div>
            <div className="face-scan-float" style={{ position: "absolute", right: "-16px", bottom: "26px", background: "var(--panel2)", border: "1px solid var(--line2)", borderRadius: "13px", padding: "12px 15px", boxShadow: "0 16px 38px rgba(0,0,0,.55)", display: "flex", alignItems: "center", gap: "11px", animation: "floaty 5s ease-in-out infinite", zIndex: 10 }}>
              <span style={{ width: "38px", height: "38px", borderRadius: "10px", background: "rgba(43,210,122,.15)", color: "var(--green)", display: "flex", alignItems: "center", justifyContent: "center", animation: "pulseRing 2.6s infinite" }}>
                {renderIcon("scanFace", 20, "text-[#2BD27A]")}
              </span>
              <div>
                <div style={{ fontSize: "12.5px", fontWeight: 700 }}>Ponto registrado</div>
                <div style={{ fontSize: "11px", color: "var(--muted)" }}>Reconhecimento facial · 08:02</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CROSSED MARQUEES (FAIXAS EM X) */}
      <div style={{ position: "relative", height: "180px", margin: "40px 0", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {/* Glow behind the cross */}
        <div style={{ position: "absolute", width: "300px", height: "300px", borderRadius: "50%", background: "radial-gradient(closest-side,rgba(59,123,255,.15),transparent)", filter: "blur(20px)", pointerEvents: "none", zIndex: 1 }}></div>

        {/* Band 1: Tilted -2.5deg, Blue accent, moving left-to-right (marquee) */}
        <div style={{
          position: "absolute",
          width: "120%",
          padding: "16px 0",
          background: "linear-gradient(90deg, rgba(14,22,38,0.92), rgba(19,31,53,0.92))",
          borderTop: "1px solid rgba(59,123,255,0.3)",
          borderBottom: "1px solid rgba(59,123,255,0.3)",
          transform: "rotate(-2.5deg)",
          overflow: "hidden",
          zIndex: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.5)"
        }}>
          <div style={{ display: "flex", width: "max-content", animation: "marquee 28s linear infinite" }}>
            <div style={{ display: "flex", gap: "38px", paddingRight: "38px" }}>
              {marqueeTexts.map((text, i) => (
                <span key={i} style={{ display: "flex", alignItems: "center", gap: "10px", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 750, fontSize: "14px", color: "#EAF0FB", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#3B7BFF", boxShadow: "0 0 8px #3B7BFF" }}></span>
                  {text}
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: "38px", paddingRight: "38px" }} aria-hidden="true">
              {marqueeTexts.map((text, i) => (
                <span key={`dup-${i}`} style={{ display: "flex", alignItems: "center", gap: "10px", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 750, fontSize: "14px", color: "#EAF0FB", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#3B7BFF", boxShadow: "0 0 8px #3B7BFF" }}></span>
                  {text}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Band 2: Tilted 2.5deg, Green accent, moving right-to-left (marqueeReverse) */}
        <div style={{
          position: "absolute",
          width: "120%",
          padding: "16px 0",
          background: "linear-gradient(90deg, rgba(14,22,38,0.92), rgba(10,19,34,0.92))",
          borderTop: "1px solid rgba(43,210,122,0.3)",
          borderBottom: "1px solid rgba(43,210,122,0.3)",
          transform: "rotate(2.5deg)",
          overflow: "hidden",
          zIndex: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.5)"
        }}>
          <div style={{ display: "flex", width: "max-content", animation: "marqueeReverse 28s linear infinite" }}>
            <div style={{ display: "flex", gap: "38px", paddingRight: "38px" }}>
              {marqueeTexts.map((text, i) => (
                <span key={i} style={{ display: "flex", alignItems: "center", gap: "10px", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 750, fontSize: "14px", color: "#EAF0FB", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#2BD27A", boxShadow: "0 0 8px #2BD27A" }}></span>
                  {text}
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: "38px", paddingRight: "38px" }} aria-hidden="true">
              {marqueeTexts.map((text, i) => (
                <span key={`dup-${i}`} style={{ display: "flex", alignItems: "center", gap: "10px", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 750, fontSize: "14px", color: "#EAF0FB", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#2BD27A", boxShadow: "0 0 8px #2BD27A" }}></span>
                  {text}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Fade gradients on the edges */}
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "160px", zIndex: 4, background: "linear-gradient(90deg,var(--bg),transparent)", pointerEvents: "none" }}></div>
        <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "160px", zIndex: 4, background: "linear-gradient(270deg,var(--bg),transparent)", pointerEvents: "none" }}></div>
      </div>

      {/* STATS */}
      <div data-reveal style={{ padding: "64px 56px 18px" }}>
        <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", border: "1px solid var(--line)", borderRadius: "18px", background: "linear-gradient(180deg,rgba(255,255,255,.035),rgba(255,255,255,.01))", overflow: "hidden" }}>
          {statsList.map((s, index) => (
            <div className="stats-item" key={index} style={{ padding: "28px 30px", borderRight: index < 3 ? "1px solid var(--line)" : "none" }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: "36px", background: "linear-gradient(100deg,var(--blue),#9DC0FF)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
                <span data-count={s.n} data-pre={s.pre} data-suf={s.suf} data-sep={s.sep ? "true" : "false"}>
                  {s.display}
                </span>
              </div>
              <div style={{ fontSize: "13.5px", color: "var(--muted)", marginTop: "5px" }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* FEATURES (RECURSOS) */}
      <div id="recursos" style={{ padding: "74px 56px" }}>
        <div data-reveal style={{ textAlign: "center", maxWidth: "680px", margin: "0 auto 48px" }}>
          <div style={{ display: "inline-block", fontSize: "12.5px", fontWeight: 700, letterSpacing: ".12em", color: "var(--blue)", textTransform: "uppercase", marginBottom: "14px" }}>
            Plataforma completa
          </div>
          <h2 style={{ fontSize: "42px", fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Tudo que seu condomínio precisa</h2>
          <p style={{ fontSize: "16.5px", color: "var(--muted)", marginTop: "14px", lineHeight: 1.6 }}>
            Funcionalidades pensadas para síndicos, administradoras e porteiros — em um único lugar, sem planilhas e sem retrabalho.
          </p>
        </div>
        <div className="features-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "18px" }}>
          {featuresList.map((f, index) => (
            <div
              key={index}
              data-reveal
              data-reveal-delay={f.dl}
              className="feature-card"
              style={{
                position: "relative",
                background: "linear-gradient(180deg,var(--panel),rgba(14,22,38,.35))",
                border: "1px solid var(--line)",
                borderRadius: "16px",
                padding: "28px",
                cursor: "pointer",
                transition: "transform .25s,border-color .25s,box-shadow .25s",
              }}
            >
              <div style={{ width: "50px", height: "50px", borderRadius: "13px", background: "var(--blueSoft)", color: "var(--blue)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "18px" }}>
                {renderIcon(f.icon, 22, "text-blue-500")}
              </div>
              <h3 style={{ fontSize: "18px", fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{f.t}</h3>
              <p style={{ fontSize: "14.5px", color: "var(--muted)", lineHeight: 1.6, marginTop: "9px" }}>{f.d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* PONTO DIGITAL */}
      <div id="ponto-digital" style={{ padding: "14px 56px 74px" }}>
        <div data-reveal style={{ position: "relative", borderRadius: "26px", overflow: "hidden", background: "linear-gradient(135deg,#0B1A2E,#0A1322)", border: "1px solid var(--line2)", padding: "56px 50px" }}>
          <div style={{ position: "absolute", width: "480px", height: "480px", right: "-80px", top: "-120px", borderRadius: "50%", background: "radial-gradient(closest-side,rgba(43,210,122,.2),transparent)", filter: "blur(10px)", animation: "aurora2 14s ease-in-out infinite", pointerEvents: "none" }}></div>
          <div className="ponto-grid" style={{ position: "relative", display: "grid", gridTemplateColumns: "1.05fr .95fr", gap: "50px", alignItems: "center" }}>
            <div>
              <div style={{ display: "inline-block", fontSize: "12.5px", fontWeight: 700, letterSpacing: ".1em", color: "var(--green)", textTransform: "uppercase", marginBottom: "14px" }}>
                Inteligência artificial + biometria
              </div>
              <h2 style={{ fontSize: "38px", fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Ponto Digital com<br />
                <span style={{ color: "var(--green)" }}>Reconhecimento Facial</span>
              </h2>
              <p style={{ fontSize: "16px", color: "var(--muted)", lineHeight: 1.6, margin: "16px 0 28px", maxWidth: "480px" }}>
                Registro de ponto seguro contra fraudes, com reconhecimento de face em tempo real, prova de vida e validação geográfica.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                {pontoFeaturesList.map((p, index) => (
                  <div key={index} style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                    <div style={{ flex: "none", width: "44px", height: "44px", borderRadius: "12px", background: "rgba(43,210,122,.13)", color: "var(--green)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {renderIcon(p.icon, 20, "text-[#2BD27A]")}
                    </div>
                    <div>
                      <div style={{ fontSize: "15.5px", fontWeight: 700 }}>{p.t}</div>
                      <div style={{ fontSize: "14px", color: "var(--muted)", lineHeight: 1.55, marginTop: "3px" }}>{p.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ position: "relative", display: "flex", justifyContent: "center" }}>
              <div style={{ position: "relative", width: "288px", borderRadius: "20px", background: "#060C16", border: "1px solid var(--line2)", padding: "18px", boxShadow: "0 28px 64px rgba(0,0,0,.6)" }}>
                <div style={{ fontSize: "11px", color: "var(--muted)", display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                  <span>ItaCondo · Ponto</span>
                  <span style={{ color: "var(--green)" }}>● ao vivo</span>
                </div>
                <div style={{ position: "relative", aspectRatio: "1", borderRadius: "14px", overflow: "hidden", background: "radial-gradient(circle at 50% 40%,rgba(43,210,122,.16),#08111d)", border: "1px solid rgba(43,210,122,.28)" }}>
                  <div style={{ position: "absolute", left: "8%", right: "8%", height: "2px", background: "linear-gradient(90deg,transparent,var(--green),transparent)", boxShadow: "0 0 12px var(--green)", animation: "scanline 2.6s linear infinite" }}></div>
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(43,210,122,.7)" }}>
                    {renderIcon("scanFace", 90, "text-green-500/50")}
                  </div>
                  <span style={{ position: "absolute", top: "10px", left: "10px", width: "18px", height: "18px", borderTop: "2px solid var(--green)", borderLeft: "2px solid var(--green)" }}></span>
                  <span style={{ position: "absolute", top: "10px", right: "10px", width: "18px", height: "18px", borderTop: "2px solid var(--green)", borderRight: "2px solid var(--green)" }}></span>
                  <span style={{ position: "absolute", bottom: "10px", left: "10px", width: "18px", height: "18px", borderBottom: "2px solid var(--green)", borderLeft: "2px solid var(--green)" }}></span>
                  <span style={{ position: "absolute", bottom: "10px", right: "10px", width: "18px", height: "18px", borderBottom: "2px solid var(--green)", borderRight: "2px solid var(--green)" }}></span>
                </div>
                <div style={{ marginTop: "14px", background: "rgba(43,210,122,.1)", border: "1px solid rgba(43,210,122,.25)", borderRadius: "11px", padding: "11px 13px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ color: "var(--green)" }}>{renderIcon("check", 20, "text-green-500")}</span>
                  <div style={{ fontSize: "12.5px" }}>
                    <b>Identidade confirmada</b>
                    <br />
                    <span style={{ color: "var(--muted)" }}>Prova de vida aprovada</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* OUTROS SERVICOS */}
      <div id="servicos" style={{ position: "relative", padding: "74px 56px", overflow: "hidden" }}>
        <div style={{ position: "absolute", width: "560px", height: "560px", left: "-160px", bottom: "-200px", borderRadius: "50%", background: "radial-gradient(closest-side,rgba(59,123,255,.12),transparent)", filter: "blur(14px)", animation: "aurora1 20s ease-in-out infinite", pointerEvents: "none" }}></div>
        <div data-reveal style={{ position: "relative", textAlign: "center", maxWidth: "700px", margin: "0 auto 48px" }}>
          <div style={{ display: "inline-block", fontSize: "12.5px", fontWeight: 700, letterSpacing: ".12em", color: "var(--blue)", textTransform: "uppercase", marginBottom: "14px" }}>
            Além do condomínio
          </div>
          <h2 style={{ fontSize: "42px", fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Outros Serviços Especializados</h2>
          <p style={{ fontSize: "16.5px", color: "var(--muted)", marginTop: "14px", lineHeight: 1.6 }}>
            Mesmo time, mesma proximidade. Tecnologia sob medida para o seu negócio — do site institucional ao BI corporativo, com transparência de valores.
          </p>
        </div>
        <div className="services-grid" style={{ position: "relative", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "16px" }}>
          {servicesList.map((s, index) => (
            <Link
              key={index}
              to={`/servicos/${s.slug}`}
              data-reveal
              data-reveal-delay={s.dl}
              className="service-card block text-white hover:text-white"
              style={{
                position: "relative",
                background: "linear-gradient(180deg,var(--panel),rgba(14,22,38,.35))",
                border: "1px solid var(--line)",
                borderRadius: "16px",
                padding: "24px",
                cursor: "pointer",
                overflow: "hidden",
                transition: "transform .25s,border-color .25s,box-shadow .25s",
                textDecoration: "none"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                <div style={{ width: "46px", height: "46px", borderRadius: "12px", background: "var(--blueSoft)", color: "var(--blue)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {renderIcon(s.icon, 20, "text-blue-500")}
                </div>
                <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: "14px", color: "rgba(255,255,255,.16)" }}>
                  {s.num}
                </span>
              </div>
              <h3 style={{ fontSize: "16px", fontWeight: 700, lineHeight: 1.25, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.t}</h3>
              <p style={{ fontSize: "13.5px", color: "var(--muted)", lineHeight: 1.55, marginTop: "8px" }}>{s.d}</p>
              <div 
                style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "16px", fontSize: "13px", fontWeight: 700, color: "var(--blue)" }}
              >
                Ver detalhes {renderIcon("arrow", 14, "text-blue-500")}
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* DIFERENCIAL */}
      <div id="diferenciais" data-reveal style={{ padding: "10px 56px 74px", textAlign: "center" }}>
        <div style={{ maxWidth: "780px", margin: "0 auto" }}>
          <div style={{ display: "inline-block", fontSize: "12.5px", fontWeight: 700, letterSpacing: ".12em", color: "var(--blue)", textTransform: "uppercase", marginBottom: "14px" }}>
            Por que a ItaCondo
          </div>
          <h2 style={{ fontSize: "40px", fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Mais que um software,<br />um parceiro de verdade
          </h2>
          <p style={{ fontSize: "17px", color: "var(--muted)", lineHeight: 1.7, marginTop: "18px" }}>
            Com mais de 10 anos de experiência, construímos relações baseadas em confiança, proximidade e amizade — acompanhando cada condomínio do início ao fim. Os 95% de satisfação e as indicações mostram que não entregamos apenas tecnologia, mas parcerias duradouras e resultados de verdade.
          </p>
        </div>
      </div>

      {/* DEPOIMENTOS (TESTIMONIALS) */}
      <div id="depoimentos" style={{ padding: "10px 16px 74px", maxWidth: "1240px", margin: "0 auto" }}>
        <div data-reveal style={{ textAlign: "center", marginBottom: "44px" }}>
          <h2 style={{ fontSize: "36px", fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Quem confia, recomenda</h2>
        </div>
        
        <div 
          data-reveal
          className="relative overflow-hidden" 
          style={{ padding: "0 48px" }}
          onMouseEnter={() => setIsAutoplayPaused(true)}
          onMouseLeave={() => setIsAutoplayPaused(false)}
        >
          {/* Arrow Buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-slate-900/60 hover:bg-slate-800/80 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition-all backdrop-blur-md hover:scale-105 active:scale-95"
            aria-label="Depoimento anterior"
          >
            <ChevronLeft size={22} />
          </button>
          
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-slate-900/60 hover:bg-slate-800/80 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition-all backdrop-blur-md hover:scale-105 active:scale-95"
            aria-label="Próximo depoimento"
          >
            <ChevronRight size={22} />
          </button>

          {/* Carousel Track Wrapper */}
          <div className="overflow-hidden">
            <div 
              className="flex items-stretch transition-transform duration-500 ease-out"
              style={{
                transform: `translateX(-${currentSlide * (100 / visibleSlides)}%)`,
              }}
            >
              {testimonialsList.map((t, index) => (
                <div
                  key={index}
                  className="shrink-0 px-2"
                  style={{
                    width: `${100 / visibleSlides}%`,
                  }}
                >
                  <div
                    className="testimonial-card h-full"
                    style={{
                      background: "var(--panel)",
                      border: "1px solid var(--line)",
                      borderRadius: "16px",
                      padding: "28px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                      transition: "transform .25s,border-color .25s",
                    }}
                  >
                    <div style={{ color: "#FFC857", fontSize: "15px", letterSpacing: "2px" }}>★★★★★</div>
                    <p style={{ fontSize: "14.8px", lineHeight: 1.65, color: "#D7E0EF", flex: 1 }}>“{t.q}”</p>
                    <div style={{ display: "flex", alignItems: "center", gap: "11px", borderTop: "1px solid var(--line)", paddingTop: "15px" }}>
                      {t.photo ? (
                        <img src={t.photo} alt={t.n} style={{ width: "42px", height: "42px", borderRadius: "50%", objectFit: "cover" }} />
                      ) : (
                        <span style={{ width: "42px", height: "42px", borderRadius: "50%", background: "linear-gradient(135deg,var(--blue),var(--green))" }}></span>
                      )}
                      <div>
                        <div style={{ fontSize: "14px", fontWeight: 700 }}>{t.n}</div>
                        <div style={{ fontSize: "12.5px", color: "var(--muted)" }}>{t.r}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: testimonialsList.length - visibleSlides + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  currentSlide === i ? "bg-blue-500 w-6" : "bg-white/20 hover:bg-white/40 w-2"
                }`}
                aria-label={`Ir para depoimento ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* CTA FORM (CONTATO) */}
      <div id="contato" style={{ padding: "14px 56px 86px" }}>
        <div data-reveal style={{ position: "relative", borderRadius: "26px", overflow: "hidden", background: "linear-gradient(120deg,#0E2A52,#0A1322 62%)", border: "1px solid var(--line2)", padding: "56px 50px" }}>
          <div style={{ position: "absolute", width: "520px", height: "520px", right: "-100px", top: "-160px", borderRadius: "50%", background: "radial-gradient(closest-side,rgba(59,123,255,.28),transparent)", filter: "blur(12px)", animation: "aurora1 16s ease-in-out infinite", pointerEvents: "none" }}></div>
          <div className="cta-grid" style={{ position: "relative", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "48px", alignItems: "center" }}>
            <div>
              <h2 style={{ fontSize: "36px", fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Pronto para simplificar<br />a gestão do seu condomínio?
              </h2>
              <p style={{ fontSize: "16px", color: "var(--muted)", lineHeight: 1.6, margin: "16px 0 26px", maxWidth: "430px" }}>
                Agende uma demonstração gratuita. Em 20 minutos mostramos como a ItaCondo se encaixa na sua rotina.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "14.5px" }}>
                  <span style={{ color: "var(--green)" }}>{renderIcon("mail", 18, "text-[#2BD27A]")}</span>
                  contatoitacondo@gmail.com
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "14.5px" }}>
                  <span style={{ color: "var(--green)" }}>{renderIcon("phone", 18, "text-[#2BD27A]")}</span>
                  +55 (21) 99823-1720
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "14.5px" }}>
                  <span style={{ color: "var(--green)" }}>{renderIcon("wa", 18, "text-[#2BD27A]")}</span>
                  Rio de Janeiro · RJ — atendemos todo o Brasil
                </div>
              </div>
            </div>
            <div style={{ background: "rgba(6,9,15,.6)", border: "1px solid var(--line2)", borderRadius: "18px", padding: "28px", backdropFilter: "blur(8px)", zIndex: 10 }}>
              <form onSubmit={handleContactSubmit}>
                <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "6px" }}>Nome</div>
                <input
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  style={{
                    width: "100%",
                    height: "48px",
                    border: "1px solid var(--line)",
                    borderRadius: "11px",
                    background: "rgba(255,255,255,.03)",
                    color: "#fff",
                    padding: "0 16px",
                    outline: "none",
                    fontSize: "14.5px",
                    marginBottom: "14px",
                  }}
                  placeholder="Seu nome completo"
                  required
                />
                
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "6px" }}>E-mail</div>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      style={{
                        width: "100%",
                        height: "48px",
                        border: "1px solid var(--line)",
                        borderRadius: "11px",
                        background: "rgba(255,255,255,.03)",
                        color: "#fff",
                        padding: "0 16px",
                        outline: "none",
                        fontSize: "14.5px",
                      }}
                      placeholder="seu@email.com"
                      required
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "6px" }}>WhatsApp</div>
                    <input
                      type="text"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      style={{
                        width: "100%",
                        height: "48px",
                        border: "1px solid var(--line)",
                        borderRadius: "11px",
                        background: "rgba(255,255,255,.03)",
                        color: "#fff",
                        padding: "0 16px",
                        outline: "none",
                        fontSize: "14.5px",
                      }}
                      placeholder="(XX) XXXXX-XXXX"
                      required
                    />
                  </div>
                </div>
                
                <div style={{ fontSize: "13px", color: "var(--muted)", margin: "14px 0 6px" }}>Condomínio</div>
                <input
                  type="text"
                  value={contactForm.condo}
                  onChange={(e) => setContactForm({ ...contactForm, condo: e.target.value })}
                  style={{
                    width: "100%",
                    height: "48px",
                    border: "1px solid var(--line)",
                    borderRadius: "11px",
                    background: "rgba(255,255,255,.03)",
                    color: "#fff",
                    padding: "0 16px",
                    outline: "none",
                    fontSize: "14.5px",
                    marginBottom: "18px",
                  }}
                  placeholder="Nome do Condomínio / Empresa"
                  required
                />
                
                <button
                  type="submit"
                  disabled={isSubmittingContact}
                  className="btn-hover"
                  style={{
                    width: "100%",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "9px",
                    height: "52px",
                    borderRadius: "12px",
                    background: "var(--blue)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "15px",
                    cursor: "pointer",
                    boxShadow: "0 12px 30px rgba(59,123,255,.42)",
                    transition: "transform .2s, box-shadow .2s",
                    border: "none",
                  }}
                >
                  <span style={{ position: "absolute", top: 0, left: 0, width: "40%", height: "100%", background: "linear-gradient(105deg,transparent,rgba(255,255,255,.5),transparent)", animation: "shimmer 3.6s infinite" }}></span>
                  {isSubmittingContact ? "Agendando..." : "Quero minha demonstração"} {renderIcon("arrow", 18)}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <AnimatedFooter
        brandName="ItaCondo"
        brandDescription="Transformando a gestão condominial com tecnologia e transparência. De gestão de condomínios a sistemas ópticos, entregamos excelência."
        brandIcon={<img src={logoSfundo} alt="itaCondo" style={{ height: "40px", width: "auto" }} />}
        socialLinks={[
          { icon: <Instagram className="w-5.5 h-5.5 text-slate-400 hover:text-white" />, href: "https://www.instagram.com/ita.condo/", label: "Instagram" },
          { icon: <Facebook className="w-5.5 h-5.5 text-slate-400 hover:text-white" />, href: "https://www.facebook.com/profile.php?viewas=100000686899395&id=61578874524877", label: "Facebook" },
        ]}
        navLinks={[
          { label: "Produtos", href: "/landing#produtos" },
          { label: "Serviços", href: "/landing#servicos" },
          { label: "Sobre Nós", href: "/landing#sobre" },
          { label: "Contato", href: "/landing#contato" },
          { label: "Política de Privacidade", href: "/politica-de-privacidade" },
          { label: "Termos de Uso", href: "/termos-de-uso" },
          { label: "Encarregado (DPO)", href: "/encarregado-de-dados" },
        ]}
        creatorName="ItaCondo Sistemas"
        creatorUrl="https://itacondo.lovable.app"
      />
    </div>
  );
};

export default Landing;
